import {MapContentLayer} from '../MapContentLayer';
import {MapContentSource} from '../MapContentSource';
import { DataDrivenPropertyValueSpecification, LayerSpecification, LineLayerSpecification } from 'maplibre-gl';
import { SubRouteItem } from '../../../../models/route';
import { LineStyle } from '../../../../models/styling';

export class ExternalConfigRouteLayer extends MapContentLayer {

  constructor(
    private routeId: number,
    private routeVisible: boolean,
    private styleOverride: LineStyle,
    private globalRouteStyle: LineStyle,
    private fallbackRouteStyle: LineStyle,
    private subRouteItems: SubRouteItem[],
    source: MapContentSource
  ) {
    super(routeId.toString(), source);
  }

  toLayerSpecification(): LayerSpecification {
    return {
      id: this.layerId,
      type: 'line',
      source: this.sourceRef.sourceId,
      filter: [
        'all',
        [ '==', ['get', 'routeId'], ['literal', this.routeId] ],
      ],
      layout: {
        visibility: this.routeVisible ? 'visible' : 'none',
        'line-join': 'miter',
        'line-cap': 'butt',
        'line-miter-limit': 2,
      },
      paint: {
        'line-color': this.getColorExpr(this.styleOverride?.color),
        'line-width': this.getWidthExpr(this.styleOverride?.width),
        'line-opacity': this.getOpacityExpr(this.styleOverride?.opacity),
        'line-dasharray': this.getLineTypeExpr(this.styleOverride?.lineType),
      }
    } as LineLayerSpecification;
  }

  getColorExpr(colorOverride: string) {
    if (this.subRouteItems && this.subRouteItems.length > 0) {
      const expr: any[] = ['case'];
      this.subRouteItems.forEach((subRouteItem) => {
        expr.push(['==', ['get', 'subRouteId'], subRouteItem.subRouteId]);
        expr.push(subRouteItem.style.color);
      });
      expr.push(this.globalRouteStyle.color);
      return expr as DataDrivenPropertyValueSpecification<string>;
    } else {
      if (colorOverride) {
        return colorOverride;
      } else {
        return this.globalRouteStyle?.color ?? this.fallbackRouteStyle.color;
      }
    }
  }

  private getWidthSubExpr(widthOverride: number) {
    if (this.subRouteItems && this.subRouteItems.length > 0) {
      const expr: any[] = ['case'];
      this.subRouteItems.forEach((subRouteItem) => {
        expr.push(['==', ['get', 'subRouteId'], subRouteItem.subRouteId]);
        expr.push(subRouteItem.style.width);
      });
      expr.push(this.globalRouteStyle.width);
      return expr;
    } else {
      if (widthOverride) {
        return widthOverride;
      } else {
        return this.globalRouteStyle?.width ?? this.fallbackRouteStyle.width;
      }
    }
  }

  getWidthExpr(widthOverride: number) {
    return [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      10, ['*', this.getWidthSubExpr(widthOverride), ['^', 2, -1]],
      24, ['*', this.getWidthSubExpr(widthOverride), ['^', 2, 8]]
    ] as DataDrivenPropertyValueSpecification<number>;
  }

  getOpacityExpr(opacityOverride: number) {
    if (this.subRouteItems && this.subRouteItems.length > 0) {
      const expr: any[] = ['case'];
      this.subRouteItems.forEach((subRouteItem) => {
        expr.push(['==', ['get', 'subRouteId'], subRouteItem.subRouteId]);
        expr.push(subRouteItem.style.opacity);
      });
      expr.push(this.globalRouteStyle.opacity);
      return expr as DataDrivenPropertyValueSpecification<number>;
    } else {
      if (opacityOverride) {
        return opacityOverride;
      } else {
        return this.globalRouteStyle?.opacity ?? this.fallbackRouteStyle.opacity;
      }
    }
  }

  getLineTypeExpr(lineTypeOverride: number[]) {
    const fallbackDashArray = [10];

    if (this.subRouteItems && this.subRouteItems.length > 0) {
      const expr: any[] = ['case'];
      this.subRouteItems.forEach((subRouteItem) => {
        expr.push(['==', ['get', 'subRouteId'], subRouteItem.subRouteId]);
        expr.push(['literal', subRouteItem.style.lineType ?? fallbackDashArray]);
      });
      expr.push([
        'literal',
        this.globalRouteStyle.lineType ?? this.fallbackRouteStyle.lineType ?? fallbackDashArray,
      ]);
      return expr as DataDrivenPropertyValueSpecification<number[]>;
    } else {
      if (lineTypeOverride) {
        return ['literal', lineTypeOverride];
      } else {
        return [
          'literal',
          this.globalRouteStyle.lineType ?? this.fallbackRouteStyle.lineType ?? fallbackDashArray,
        ] as DataDrivenPropertyValueSpecification<number[]>;
      }
    }
  }

  updateGlobalStyle(style: LineStyle) {
    this.globalRouteStyle = style;
  }
}
